import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import * as Dialog from '@radix-ui/react-dialog';
import * as Select from '@radix-ui/react-select';
import { Link } from 'react-router-dom';
import type { PersonResponse } from 'we-tree-api-client/v2';
import type { LivingStatusType } from 'we-tree-api-client/v2';

import { useDomainStore, useSuggestionsStore } from '@/hooks/useStores';
import DateFieldWithPrecision from '@/components/shared/DateFieldWithPrecision';
import SuggestionDiffPreview from '@/components/suggestions/SuggestionDiffPreview';
import SuggestFactsLifeEventsSection from '@/components/suggestions/SuggestFactsLifeEventsSection';
import SuggestEvidenceStep from '@/components/suggestions/SuggestEvidenceStep';
import { GENDER_VALUES } from '@/types/gender';
import {
  EMPTY_SUGGEST_FACTS_EVENTS,
  type SuggestFactsLifeEventsState,
  type SuggestionEvidenceDraft,
  type CreatePersonSuggestionResult,
  buildPersonDiffPayload,
  buildFactDiffOps,
  buildLifeEventDiffOps,
  mergeSuggestionDiffPayload,
  diffPayloadHasChanges,
  collectDiffTargetPaths,
  personToSuggestFieldValues,
  getSuggestionStaleTargetComposerMessage,
  type PersonSuggestFieldValues
} from '@/types/suggestion';
import { formatDateForInput, parseDateOnly } from '@/utils/dateFormatting';
import type { DatePrecision } from '@/utils/dateFormatting';
import { getUpgradeAwareErrorParts, isFamilyOwner } from '@/utils/familyPlan';

import '../person/person-edit-form.css';
import './suggest-changes-modal.css';

type ModalStep = 'edit' | 'preview' | 'evidence';

export interface SuggestChangesModalProps {
  familyId: string;
  personData: PersonResponse;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onRequestUpgrade?: () => void;
}

const SuggestChangesModal: React.FC<SuggestChangesModalProps> = observer(
  ({ familyId, personData, isOpen, onClose, onSuccess, onRequestUpgrade }) => {
    const { t } = useTranslation('forms');
    const { t: tCommon } = useTranslation('common');
    const domainStore = useDomainStore();
    const suggestionsStore = useSuggestionsStore();
    const isOwner = isFamilyOwner(domainStore.currentFamily?.userRole);

    const initialValues = useMemo(() => personToSuggestFieldValues(personData), [personData]);
    const initialBirthDate = useMemo(() => formatDateForInput(personData.birthDate) || '', [personData.birthDate]);
    const initialDeathDate = useMemo(() => formatDateForInput(personData.deathDate) || '', [personData.deathDate]);

    const [step, setStep] = useState<ModalStep>('edit');
    const [fieldValues, setFieldValues] = useState<PersonSuggestFieldValues>(initialValues);
    const [isDeceased, setIsDeceased] = useState(personData.livingStatus === 'deceased');
    const [description, setDescription] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [upgradeHint, setUpgradeHint] = useState<string | null>(null);
    const [factsLifeEvents, setFactsLifeEvents] = useState<SuggestFactsLifeEventsState>(EMPTY_SUGGEST_FACTS_EVENTS);
    const [evidenceDrafts, setEvidenceDrafts] = useState<SuggestionEvidenceDraft[]>([]);
    const [evidenceWarning, setEvidenceWarning] = useState<string | null>(null);
    const [staleTargetWarning, setStaleTargetWarning] = useState<string | null>(null);

    useEffect(() => {
      if (!isOpen) return;
      setStep('edit');
      setFieldValues(personToSuggestFieldValues(personData));
      setIsDeceased(personData.livingStatus === 'deceased');
      setDescription('');
      setErrors({});
      setSubmitError(null);
      setUpgradeHint(null);
      setFactsLifeEvents(EMPTY_SUGGEST_FACTS_EVENTS);
      setEvidenceDrafts([]);
      setEvidenceWarning(null);
      setStaleTargetWarning(null);
      suggestionsStore.clearError();
    }, [isOpen, personData, suggestionsStore]);

    const refreshStaleTargetWarning = async () => {
      const freshness = await suggestionsStore.checkPersonTargetFreshness(
        personData.familyMemberId,
        personData.updatedAt
      );
      if (freshness.stale) {
        setStaleTargetWarning(getSuggestionStaleTargetComposerMessage());
        return true;
      }
      setStaleTargetWarning(null);
      return false;
    };

    const previewPayload = useMemo(() => {
      const personPayload = buildPersonDiffPayload(personData, {
        ...fieldValues,
        birthDate: fieldValues.birthDate || initialBirthDate,
        deathDate: isDeceased ? fieldValues.deathDate || initialDeathDate : '',
        deathDatePrecision: isDeceased ? fieldValues.deathDatePrecision : '',
        deathPlace: isDeceased ? fieldValues.deathPlace : '',
        livingStatus: (isDeceased ? 'deceased' : 'living') as LivingStatusType
      });
      const factOps = buildFactDiffOps(
        factsLifeEvents.factCreates,
        factsLifeEvents.factUpdates,
        factsLifeEvents.factDeletes
      );
      const lifeEventOps = buildLifeEventDiffOps(
        factsLifeEvents.lifeEventCreates,
        factsLifeEvents.lifeEventUpdates,
        factsLifeEvents.lifeEventDeletes
      );
      return mergeSuggestionDiffPayload(personPayload, factOps, lifeEventOps);
    }, [personData, fieldValues, isDeceased, initialBirthDate, initialDeathDate, factsLifeEvents]);

    const evidenceTargetPaths = useMemo(() => collectDiffTargetPaths(previewPayload), [previewPayload]);

    const validate = (): boolean => {
      const nextErrors: Record<string, string> = {};
      if (!fieldValues.givenName.trim()) {
        nextErrors.givenName = t('person.givenNameRequiredError');
      }
      if (fieldValues.birthDate && isDeceased && fieldValues.deathDate) {
        const pb = parseDateOnly(fieldValues.birthDate);
        const pd = parseDateOnly(fieldValues.deathDate);
        if (pb && pd) {
          const birthCompare = pb.year * 10000 + pb.month * 100 + pb.day;
          const deathCompare = pd.year * 10000 + pd.month * 100 + pd.day;
          if (deathCompare < birthCompare) {
            nextErrors.deathDate = t('person.deathAfterBirth');
          }
        }
      }
      setErrors(nextErrors);
      return Object.keys(nextErrors).length === 0;
    };

    const handleContinueToPreview = async (event: React.FormEvent) => {
      event.preventDefault();
      setSubmitError(null);
      setUpgradeHint(null);
      if (!validate()) return;

      if (!diffPayloadHasChanges(previewPayload)) {
        setSubmitError(t('suggest.changeAtLeastOneField'));
        return;
      }

      await refreshStaleTargetWarning();
      setStep('preview');
    };

    const handleSubmit = async (withEvidence: boolean) => {
      setSubmitError(null);
      setUpgradeHint(null);
      setEvidenceWarning(null);
      await refreshStaleTargetWarning();

      const result = (await suggestionsStore.createPersonSuggestion(familyId, {
        familyMemberId: personData.familyMemberId,
        personId: personData.personId,
        description,
        originalPerson: personData,
        fieldValues: {
          ...fieldValues,
          livingStatus: (isDeceased ? 'deceased' : 'living') as LivingStatusType,
          deathDate: isDeceased ? fieldValues.deathDate : '',
          deathDatePrecision: isDeceased ? fieldValues.deathDatePrecision : '',
          deathPlace: isDeceased ? fieldValues.deathPlace : ''
        },
        factCreates: factsLifeEvents.factCreates,
        factUpdates: factsLifeEvents.factUpdates,
        factDeletes: factsLifeEvents.factDeletes,
        lifeEventCreates: factsLifeEvents.lifeEventCreates,
        lifeEventUpdates: factsLifeEvents.lifeEventUpdates,
        lifeEventDeletes: factsLifeEvents.lifeEventDeletes,
        evidence: withEvidence ? evidenceDrafts : undefined
      })) as CreatePersonSuggestionResult;

      if (result.success) {
        if (result.evidenceFailed) {
          setEvidenceWarning(result.evidenceErrorMessage ?? t('suggest.evidenceAttachFailed'));
          return;
        }
        onSuccess?.();
        onClose();
        return;
      }

      const parts = getUpgradeAwareErrorParts(
        result.error as Parameters<typeof getUpgradeAwareErrorParts>[0],
        isOwner,
        t('suggest.submitFailed')
      );
      setSubmitError(parts.message);
      setUpgradeHint(parts.upgradeHint);
    };

    const displayName =
      `${personData.givenName} ${personData.surname ?? ''}`.trim() || personData.preferredName || tCommon('someone');

    return (
      <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <Dialog.Portal>
          <Dialog.Overlay className="suggest-changes-modal__overlay" />
          <Dialog.Content className="suggest-changes-modal" aria-describedby="suggest-changes-desc">
            <div className="suggest-changes-modal__header">
              <Dialog.Title className="suggest-changes-modal__title">{t('person.suggestChanges')}</Dialog.Title>
              <Dialog.Close type="button" className="suggest-changes-modal__close" aria-label={tCommon('close')}>
                ×
              </Dialog.Close>
            </div>

            <p id="suggest-changes-desc" className="suggest-changes-modal__lede">
              Propose edits to {displayName}. An editor will review your suggestion before anything is applied.
            </p>

            {staleTargetWarning && (
              <div className="suggest-changes-modal__warning" role="status" data-testid="suggest-stale-warning">
                {staleTargetWarning}
              </div>
            )}

            {submitError && (
              <div className="suggest-changes-modal__error" role="alert">
                <span>{submitError}</span>
                {upgradeHint && (
                  <span className="suggest-changes-modal__upgrade-hint">
                    {upgradeHint}{' '}
                    {onRequestUpgrade && isOwner && (
                      <button type="button" className="suggest-changes-modal__upgrade-link" onClick={onRequestUpgrade}>
                        {t('actions.upgradeNow')}
                      </button>
                    )}
                  </span>
                )}
              </div>
            )}

            {evidenceWarning && (
              <div className="suggest-changes-modal__warning" role="status">
                <span>{evidenceWarning}</span>
                <span className="suggest-changes-modal__upgrade-hint">
                  <Link to={`/families/${familyId}/suggestions`} onClick={onClose}>
                    Open My suggestions
                  </Link>
                </span>
                <div className="suggest-changes-modal__actions">
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => {
                      onSuccess?.();
                      onClose();
                    }}
                  >
                    {tCommon('done')}
                  </button>
                </div>
              </div>
            )}

            {!evidenceWarning && step === 'edit' ? (
              <form onSubmit={handleContinueToPreview} className="person-edit-form suggest-changes-modal__form">
                <div className="person-edit-form__section">
                  <h3 className="person-edit-form__section-title">{t('person.basicInformation')}</h3>
                  <div className="person-edit-form__field-group">
                    <div className="person-edit-form__field">
                      <label htmlFor="suggest-givenName" className="person-edit-form__label">
                        {t('person.givenNameRequired')}
                      </label>
                      <input
                        id="suggest-givenName"
                        type="text"
                        value={fieldValues.givenName}
                        onChange={(e) => setFieldValues((prev) => ({ ...prev, givenName: e.target.value }))}
                        className={`person-edit-form__input ${errors.givenName ? 'person-edit-form__input--error' : ''}`}
                        required
                      />
                      {errors.givenName && <span className="person-edit-form__error-message">{errors.givenName}</span>}
                    </div>
                    <div className="person-edit-form__field">
                      <label htmlFor="suggest-middleName" className="person-edit-form__label">
                        {t('person.middleName')}
                      </label>
                      <input
                        id="suggest-middleName"
                        type="text"
                        value={fieldValues.middleName}
                        onChange={(e) => setFieldValues((prev) => ({ ...prev, middleName: e.target.value }))}
                        className="person-edit-form__input"
                      />
                    </div>
                    <div className="person-edit-form__field">
                      <label htmlFor="suggest-surname" className="person-edit-form__label">
                        {t('person.surname')}
                      </label>
                      <input
                        id="suggest-surname"
                        type="text"
                        value={fieldValues.surname}
                        onChange={(e) => setFieldValues((prev) => ({ ...prev, surname: e.target.value }))}
                        className="person-edit-form__input"
                      />
                    </div>
                  </div>

                  <div className="person-edit-form__field-group">
                    <div className="person-edit-form__field">
                      <label htmlFor="suggest-suffix" className="person-edit-form__label">
                        {t('person.suffix')}
                      </label>
                      <input
                        id="suggest-suffix"
                        type="text"
                        value={fieldValues.suffix}
                        onChange={(e) => setFieldValues((prev) => ({ ...prev, suffix: e.target.value }))}
                        className="person-edit-form__input"
                      />
                    </div>
                    <div className="person-edit-form__field">
                      <label htmlFor="suggest-preferredName" className="person-edit-form__label">
                        {t('person.preferredName')}
                      </label>
                      <input
                        id="suggest-preferredName"
                        type="text"
                        value={fieldValues.preferredName}
                        onChange={(e) => setFieldValues((prev) => ({ ...prev, preferredName: e.target.value }))}
                        className="person-edit-form__input"
                      />
                    </div>
                    <div className="person-edit-form__field">
                      <label htmlFor="suggest-gender" className="person-edit-form__label">
                        {t('person.gender')}
                      </label>
                      <Select.Root
                        value={fieldValues.gender || 'unknown'}
                        onValueChange={(value) =>
                          setFieldValues((prev) => ({ ...prev, gender: value as PersonResponse['gender'] }))
                        }
                      >
                        <Select.Trigger
                          id="suggest-gender"
                          className="form-select-trigger"
                          aria-label={t('person.gender')}
                        >
                          <Select.Value />
                        </Select.Trigger>
                        <Select.Portal>
                          <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                            {GENDER_VALUES.map((gender) => (
                              <Select.Item key={gender} value={gender} className="form-select-item">
                                <Select.ItemText>{t(`gender.${gender}`)}</Select.ItemText>
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Portal>
                      </Select.Root>
                    </div>
                  </div>
                </div>

                <div className="person-edit-form__section">
                  <h3 className="person-edit-form__section-title">{t('person.birthInformation')}</h3>
                  <div className="person-edit-form__field-group">
                    <DateFieldWithPrecision
                      id="suggest-birthDate"
                      label={t('person.birthDate')}
                      value={fieldValues.birthDate}
                      precision={fieldValues.birthDatePrecision as DatePrecision}
                      onValueChange={(value) => setFieldValues((prev) => ({ ...prev, birthDate: value }))}
                      onPrecisionChange={(precision) =>
                        setFieldValues((prev) => ({ ...prev, birthDatePrecision: precision }))
                      }
                      onErrorClear={() =>
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.birthDate;
                          return next;
                        })
                      }
                      error={errors.birthDate}
                      allowClearViaInput={false}
                      layout="inline"
                    />
                    <div className="person-edit-form__field">
                      <label htmlFor="suggest-birthPlace" className="person-edit-form__label">
                        {t('person.birthPlace')}
                      </label>
                      <input
                        id="suggest-birthPlace"
                        type="text"
                        value={fieldValues.birthPlace}
                        onChange={(e) => setFieldValues((prev) => ({ ...prev, birthPlace: e.target.value }))}
                        className="person-edit-form__input"
                      />
                    </div>
                  </div>
                </div>

                <div className="person-edit-form__section">
                  <h3 className="person-edit-form__section-title">{t('person.deathInformation')}</h3>
                  <div className="person-edit-form__field-group">
                    <div className="person-edit-form__field person-edit-form__field--full">
                      <label className="person-edit-form__label person-edit-form__checkbox-label">
                        <input
                          type="checkbox"
                          checked={isDeceased}
                          onChange={(e) => {
                            setIsDeceased(e.target.checked);
                            if (!e.target.checked) {
                              setFieldValues((prev) => ({ ...prev, deathDate: '', deathPlace: '' }));
                            }
                          }}
                          className="person-edit-form__checkbox"
                        />
                        <span>{t('person.deceased')}</span>
                      </label>
                    </div>
                  </div>
                  {isDeceased && (
                    <div className="person-edit-form__field-group">
                      <DateFieldWithPrecision
                        id="suggest-deathDate"
                        label={t('person.deathDate')}
                        value={fieldValues.deathDate}
                        precision={fieldValues.deathDatePrecision as DatePrecision}
                        onValueChange={(value) => setFieldValues((prev) => ({ ...prev, deathDate: value }))}
                        onPrecisionChange={(precision) =>
                          setFieldValues((prev) => ({ ...prev, deathDatePrecision: precision }))
                        }
                        onErrorClear={() =>
                          setErrors((prev) => {
                            const next = { ...prev };
                            delete next.deathDate;
                            return next;
                          })
                        }
                        error={errors.deathDate}
                        allowClearViaInput
                        layout="inline"
                      />
                      <div className="person-edit-form__field">
                        <label htmlFor="suggest-deathPlace" className="person-edit-form__label">
                          {t('person.deathPlace')}
                        </label>
                        <input
                          id="suggest-deathPlace"
                          type="text"
                          value={fieldValues.deathPlace}
                          onChange={(e) => setFieldValues((prev) => ({ ...prev, deathPlace: e.target.value }))}
                          className="person-edit-form__input"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <SuggestFactsLifeEventsSection
                  familyMemberId={personData.familyMemberId}
                  value={factsLifeEvents}
                  onChange={setFactsLifeEvents}
                />

                <div className="suggest-changes-modal__actions">
                  <button type="button" className="btn btn--ghost" onClick={onClose}>
                    {tCommon('cancel')}
                  </button>
                  <button type="submit" className="btn btn--primary">
                    {t('actions.previewChanges')}
                  </button>
                </div>
              </form>
            ) : !evidenceWarning && step === 'preview' ? (
              <div className="suggest-changes-modal__preview">
                <h3 className="suggest-changes-modal__preview-title">Proposed changes</h3>
                <SuggestionDiffPreview diffPayload={previewPayload} />

                <div className="person-edit-form__field suggest-changes-modal__note-field">
                  <label htmlFor="suggest-description" className="person-edit-form__label">
                    Note for reviewers (optional)
                  </label>
                  <textarea
                    id="suggest-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="person-edit-form__input suggest-changes-modal__textarea"
                    rows={3}
                    maxLength={2000}
                  />
                </div>

                <div className="suggest-changes-modal__actions">
                  <button type="button" className="btn btn--ghost" onClick={() => setStep('edit')}>
                    {tCommon('back')}
                  </button>
                  <button type="button" className="btn btn--primary" onClick={() => setStep('evidence')}>
                    Continue
                  </button>
                </div>
              </div>
            ) : !evidenceWarning ? (
              <div className="suggest-changes-modal__preview">
                <SuggestEvidenceStep
                  familyId={familyId}
                  targetPaths={evidenceTargetPaths}
                  drafts={evidenceDrafts}
                  onChange={setEvidenceDrafts}
                />

                <div className="suggest-changes-modal__actions">
                  <button type="button" className="btn btn--ghost" onClick={() => setStep('preview')}>
                    {tCommon('back')}
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    disabled={suggestionsStore.isSubmitting}
                    onClick={() => void handleSubmit(false)}
                  >
                    {t('actions.skipEvidence')}
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={suggestionsStore.isSubmitting}
                    onClick={() => void handleSubmit(true)}
                  >
                    {suggestionsStore.isSubmitting ? t('actions.submitting') : t('actions.submitSuggestion')}
                  </button>
                </div>
              </div>
            ) : null}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }
);

export default SuggestChangesModal;
